/**
 * Baloanele Bootstrap 3 (`$('[data-toggle="tooltip"]').tooltip()`, chemat in
 * portal la fiecare desenare de tabel), fara jQuery: un singur ascultator pe
 * document, care la trecerea peste un element cu `data-toggle="tooltip"`
 * pune in <body> exact markupul lui Bootstrap -
 * `div.tooltip.fade.{placement}.in > .tooltip-arrow + .tooltip-inner` -
 * si il asaza cu aceleasi formule (Tooltip.prototype.getCalculatedOffset).
 * Foaia portalului (app.css) il deseneaza; aici nu e niciun stil.
 *
 * Ca Bootstrap, `title` se muta in `data-original-title` si ramane gol, ca
 * browserul sa nu mai arate si baloanul lui.
 */

export type Placement = 'top' | 'bottom' | 'left' | 'right'

const SELECTOR = '[data-toggle="tooltip"], [rel="tooltip"]'

let current: { target: Element; tip: HTMLDivElement } | null = null

export function tooltipTitle(el: Element): string {
  const title = el.getAttribute('title')
  if (title !== null && title !== '') {
    el.setAttribute('data-original-title', title)
    el.setAttribute('title', '')
    return title
  }
  return el.getAttribute('data-original-title') ?? ''
}

export function placementOf(el: Element): Placement {
  const value = el.getAttribute('data-placement')
  return value === 'bottom' || value === 'left' || value === 'right' ? value : 'top'
}

/** Tooltip.prototype.getCalculatedOffset, in coordonatele documentului. */
export function calculatedOffset(
  placement: Placement,
  pos: { top: number; left: number; width: number; height: number },
  actualWidth: number,
  actualHeight: number,
): { top: number; left: number } {
  switch (placement) {
    case 'bottom':
      return { top: pos.top + pos.height, left: pos.left + pos.width / 2 - actualWidth / 2 }
    case 'left':
      return { top: pos.top + pos.height / 2 - actualHeight / 2, left: pos.left - actualWidth }
    case 'right':
      return { top: pos.top + pos.height / 2 - actualHeight / 2, left: pos.left + pos.width }
    default:
      return { top: pos.top - actualHeight, left: pos.left + pos.width / 2 - actualWidth / 2 }
  }
}

export function hideTooltip(): void {
  if (!current) return
  current.tip.remove()
  current = null
}

export function showTooltip(target: Element): void {
  hideTooltip()
  const title = tooltipTitle(target)
  if (title === '') return
  const placement = placementOf(target)

  const tip = document.createElement('div')
  tip.className = `tooltip fade ${placement}`
  tip.setAttribute('role', 'tooltip')
  const arrow = document.createElement('div')
  arrow.className = 'tooltip-arrow'
  const inner = document.createElement('div')
  inner.className = 'tooltip-inner'
  inner.textContent = title
  tip.append(arrow, inner)
  tip.style.top = '0px'
  tip.style.left = '0px'
  document.body.appendChild(tip)

  const rect = target.getBoundingClientRect()
  const pos = { top: rect.top + window.scrollY, left: rect.left + window.scrollX, width: rect.width, height: rect.height }
  const offset = calculatedOffset(placement, pos, tip.offsetWidth, tip.offsetHeight)
  tip.style.top = `${Math.round(offset.top)}px`
  tip.style.left = `${Math.round(offset.left)}px`
  tip.classList.add('in')
  current = { target, tip }
}

function onOver(event: Event): void {
  const target = (event.target as Element | null)?.closest?.(SELECTOR)
  if (current && current.target === target) return
  /* Elementul de sub balon poate fi redesenat de React fara mouseout; cand
     mouse-ul e deja pe altceva, baloanul vechi pleaca. */
  if (current && !current.target.isConnected) hideTooltip()
  if (!target) {
    if (current && !current.target.contains(event.target as Node)) hideTooltip()
    return
  }
  showTooltip(target)
}

function onOut(event: Event): void {
  if (!current) return
  const related = (event as MouseEvent).relatedTarget as Node | null
  if (related && current.target.contains(related)) return
  const from = (event.target as Element | null)?.closest?.(SELECTOR)
  if (from === current.target) hideTooltip()
}

let installed = false

/** O singura data pe pagina; se poate chema de oriunde. */
export function installTooltips(): () => void {
  if (installed) return () => {}
  installed = true
  document.addEventListener('mouseover', onOver)
  document.addEventListener('mouseout', onOut)
  return () => {
    installed = false
    hideTooltip()
    document.removeEventListener('mouseover', onOver)
    document.removeEventListener('mouseout', onOut)
  }
}
